import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyScheduleTasks } from '../services/scheduleService';
import { getDepartments } from '../services/employeeService';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { SCHEDULE_STATUS_COLORS, SCHEDULE_STATUS_LABELS, formatSlip } from '../constants/schedule';
import ScheduleTaskForm from '../components/ScheduleTaskForm';

// Department head's weekly update: every open schedule line of their
// department(s) across all active projects, overdue first. CEO/System_Admin
// can switch to any department. The per-line form (validation, status/% kept
// in step) is ScheduleTaskForm, shared with the project page.
const ADMIN_ROLES = ['CEO', 'System_Admin'];

const MyScheduleTasksPage = () => {
  const user = useAuthStore((s) => s.user);
  const showToast = useUIStore((s) => s.showToast);
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [departmentId, setDepartmentId] = useState('');
  const [allDepartments, setAllDepartments] = useState([]);
  const [result, setResult] = useState({ tasks: [], departments: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getMyScheduleTasks(departmentId ? { department_id: departmentId } : {})
      .then((res) => setResult(res.data))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load tasks', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin) getDepartments().then((res) => setAllDepartments(res.data.departments || [])).catch(() => {});
  }, [isAdmin]);

  const grouped = useMemo(() => {
    const groups = [];
    result.tasks.forEach((t) => {
      let g = groups.find((x) => x.project_id === t.project_id);
      if (!g) {
        g = { project_id: t.project_id, project_name: t.project_name, client_name: t.client_name, tasks: [] };
        groups.push(g);
      }
      g.tasks.push(t);
    });
    return groups;
  }, [result.tasks]);

  const overdueCount = result.tasks.filter((t) => t.is_overdue).length;
  const departmentNames = result.departments.map((d) => d.name).join(', ');

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Schedule Updates</h1>
            <p className="text-sm text-white/80 mt-1">
              {departmentNames ? `${departmentNames}: ` : ''}{result.tasks.length} open tasks
              {overdueCount > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold">{overdueCount} overdue</span>}
            </p>
          </div>
          {isAdmin && (
            <select className="border border-gray-300 rounded px-3 py-2 text-sm" value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">Departments I head</option>
              {allDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-gray-500">Loading...</div>
        ) : result.tasks.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">
            {result.departments.length === 0 && !departmentId
              ? "You aren't set as head or manager of any department, so there's nothing to update here."
              : 'No open schedule tasks for this department.'}
          </div>
        ) : (
          grouped.map((g) => (
            <div key={g.project_id} className="glass-card p-5 mb-4">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                <Link to={`/projects/${g.project_id}`} className="font-semibold text-gray-900 hover:underline">{g.project_name}</Link>
                {g.client_name && <span className="text-xs text-gray-500">{g.client_name}</span>}
              </div>
              {g.tasks.map((t) => (
                <TaskRow key={`${t.id}-${t.updated_at}`} task={t} showDepartment={result.departments.length > 1} onSaved={load} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const TaskRow = ({ task, showDepartment, onSaved }) => (
  <div className={`border-t border-gray-100 py-3 ${task.is_overdue ? 'bg-red-50 -mx-5 px-5' : ''}`}>
    <div className="flex justify-between flex-wrap gap-2 mb-2">
      <div className="text-sm">
        <span className="font-medium">{task.step_seq}. {task.step_name}</span>
        {task.is_milestone && <span className="text-xs text-gray-400 ml-1">(milestone)</span>}
        {showDepartment && <span className="text-xs text-gray-500 ml-2">{task.department_name}</span>}
        {task.responsible_name && <span className="text-xs text-gray-500 ml-2">Responsible: {task.responsible_name}</span>}
        <span className={`ml-2 text-xs px-2 py-0.5 rounded font-semibold ${SCHEDULE_STATUS_COLORS[task.status]}`}>{SCHEDULE_STATUS_LABELS[task.status]}</span>
        {task.is_overdue && <span className="ml-2 text-xs font-semibold text-red-700">Overdue</span>}
      </div>
      <div className="text-xs text-gray-500">
        Baseline {task.is_milestone ? formatDateDDMMYY(task.baseline_end) : `${formatDateDDMMYY(task.baseline_start)} to ${formatDateDDMMYY(task.baseline_end)}`}
        {' · '}Step forecast {formatDateDDMMYY(task.projected_end)}{' '}
        <span className={task.slip_days > 0 ? 'text-red-700 font-semibold' : ''}>({formatSlip(task.slip_days)})</span>
      </div>
    </div>
    <ScheduleTaskForm task={task} onSaved={onSaved} successLabel={task.step_name} />
  </div>
);

export default MyScheduleTasksPage;
